import { useState, useEffect } from 'react';
import {
	Mail,
	Send,
	AlertCircle,
	CheckCircle,
	X,
	ExternalLink,
	ShoppingCart,
	ChevronDown,
	Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select';
import Link from 'next/link';
import { EmailDomain } from '@prisma/client';

interface EmailSendModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSend: () => Promise<void>;
	selectedCount: number;
	totalCount: number;
	isSelected: boolean;
	isEmailing: boolean;

	// Email configuration
	emailName: string;
	setEmailName: (name: string) => void;
	emailDomain: string;
	setEmailDomain: (domain: string) => void;
	emailSignature: string;
	setEmailSignature: (signature: string) => void;
	domains: EmailDomain[];

	// User session for auth checks
	isSignedIn: boolean;

	// Auto-open logic
	shouldAutoOpen?: boolean;
}

interface ChecklistItem {
	id: string;
	label: string;
	status: 'pending' | 'complete' | 'error';
	description?: string;
	actionButton?: {
		text: string;
		href: string;
		variant?: 'default' | 'outline';
	};
}

export function EmailSendModal({
	isOpen,
	onClose,
	onSend,
	selectedCount,
	totalCount,
	isSelected,
	isEmailing,
	emailName,
	setEmailName,
	emailDomain,
	setEmailDomain,
	emailSignature,
	setEmailSignature,
	domains,
	isSignedIn,
	shouldAutoOpen = false
}: EmailSendModalProps) {
	const [currentStep, setCurrentStep] = useState<'setup' | 'sending'>('setup');
	const [sendingProgress, setSendingProgress] = useState(0);

	// Simple validation - just check if we have the basics
	const hasName = emailName.trim().length > 0;
	const hasSignature = emailSignature.trim().length > 0;
	const hasDomain = domains.length > 0;
	const hasCustomDomain = domains.some((d) => d.domain !== 'oxide.email');
	const onlyOxideEmail = domains.length === 1 && domains[0]?.domain === 'oxide.email';
	// Allow sending with any domain (including oxide.email) if user has name and signature
	const canSend = hasName && hasSignature && hasDomain;

	// Auto-generate signature when domain or name changes
	useEffect(() => {
		if (emailName && emailDomain && (!emailSignature || emailSignature === '')) {
			setEmailSignature(`Best regards,\n${emailName}\nCo-Founder at https://${emailDomain}`);
		}
	}, [emailName, emailDomain, emailSignature, setEmailSignature]);

	// Handle sending process
	const handleSend = async () => {
		if (!canSend) return;

		setCurrentStep('sending');
		setSendingProgress(0);

		// Simulate progress during sending
		const progressInterval = setInterval(() => {
			setSendingProgress((prev) => {
				if (prev >= 90) {
					clearInterval(progressInterval);
					return 90; // Don't complete until actual send is done
				}
				return prev + 10;
			});
		}, 200);

		try {
			await onSend();
			setSendingProgress(100);

			// Close modal after successful send
			setTimeout(() => {
				onClose();
				setCurrentStep('setup');
				setSendingProgress(0);
			}, 1500);
		} catch (error) {
			setSendingProgress(0);
			setCurrentStep('setup');
		}

		clearInterval(progressInterval);
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
				<DialogHeader className="flex-shrink-0">
					<DialogTitle className="flex items-center gap-2">
						<Mail className="h-5 w-5" />
						{currentStep === 'setup'
							? shouldAutoOpen
								? 'Send Email to Start Optimizing Campaign'
								: `Send ${isSelected ? 'Selected' : 'All'} Emails`
							: 'Sending Emails...'}
					</DialogTitle>
				</DialogHeader>

				{currentStep === 'setup' ? (
					<>
						{/* Scrollable Content Area */}
						<div className="flex-1 overflow-y-auto space-y-6 pr-2">
							{/* Call to Action for auto-opened modal */}
							{shouldAutoOpen && (
								<div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
									<h3 className="font-semibold text-emerald-900 mb-2">
										Ready to launch your campaign?
									</h3>
									<p className="text-emerald-700 text-sm">
										Complete your email configuration below to start sending
										personalized outreach emails.
									</p>
								</div>
							)}

							{/* Upgrade to custom domain for signed-in users - moved here for prominence */}
							{onlyOxideEmail && isSignedIn && (
								<div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
									<div className="text-center space-y-4">
										<div>
											<ShoppingCart className="h-10 w-10 mx-auto mb-3 text-emerald-600" />
											<h4 className="text-lg font-bold text-emerald-900 mb-2">
												Upgrade to Professional Domain
											</h4>
											<p className="text-sm text-emerald-800 mb-4 leading-relaxed">
												You're using oxide.email for testing. Get a custom
												domain to be more professional and improve
												deliverability.
											</p>
										</div>

										{/* Benefits List */}
										<div className="bg-white/80 rounded-lg p-4 mb-4">
											<div className="grid grid-cols-1 gap-3 text-left">
												<div className="flex items-center gap-3">
													<div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
													<span className="text-sm font-medium text-gray-700">
														Higher Email Deliverability
													</span>
												</div>
												<div className="flex items-center gap-3">
													<div className="w-2 h-2 bg-teal-500 rounded-full"></div>
													<span className="text-sm font-medium text-gray-700">
														Professional Brand Image
													</span>
												</div>
												<div className="flex items-center gap-3">
													<div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
													<span className="text-sm font-medium text-gray-700">
														Better Trust & Credibility
													</span>
												</div>
											</div>
										</div>

										<Button
											size="lg"
											className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 text-base"
											asChild
										>
											<Link href="/domains">
												<ShoppingCart className="h-5 w-5 mr-2" />
												Buy Professional Domain
											</Link>
										</Button>
										<p className="text-xs text-emerald-600 mt-2">
											⚠️ oxide.email has lower deliverability than custom
											domains
										</p>
									</div>
								</div>
							)}

							{/* Email Configuration Form */}
							<div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
								<h3 className="text-lg font-semibold mb-4 text-gray-900 border-b border-gray-200 pb-2">
									Email Configuration
								</h3>

								<div className="space-y-4">
									{/* Domain Selection */}
									<div className="space-y-3">
										<label className="text-sm font-medium text-gray-700">
											Sending Domain
										</label>

										{hasDomain ? (
											<div className="space-y-3">
												<Select
													value={emailDomain}
													onValueChange={setEmailDomain}
												>
													<SelectTrigger className="w-full">
														<SelectValue placeholder="Select a sending domain" />
													</SelectTrigger>
													<SelectContent>
														{domains.map((domain) => (
															<SelectItem
																key={domain.domain}
																value={domain.domain}
															>
																<div className="flex items-center justify-between w-full">
																	<span>{domain.domain}</span>
																	{domain.domain ===
																		'oxide.email' && (
																		<Badge
																			variant="secondary"
																			className="ml-2 text-xs"
																		>
																			Trial
																		</Badge>
																	)}
																</div>
															</SelectItem>
														))}
													</SelectContent>
												</Select>

												{emailDomain && (
													<div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
														<div className="flex items-center gap-2">
															<CheckCircle className="h-5 w-5 text-emerald-600" />
															<span className="text-emerald-800 font-medium">
																Emails will be sent from:{' '}
																{emailName?.toLowerCase()}@
																{emailDomain}
															</span>
														</div>
													</div>
												)}
											</div>
										) : (
											<div className="bg-red-50 border border-red-200 rounded-lg p-4">
												<div className="text-center">
													<ShoppingCart className="h-8 w-8 mx-auto mb-2 text-red-600" />
													<p className="text-sm text-red-700">
														You need a custom domain to send
														professional emails.
													</p>
												</div>
											</div>
										)}
									</div>

									{/* Sender Name */}
									<div className="space-y-2">
										<label className="text-sm font-medium text-gray-700">
											Sender Name
										</label>
										<Input
											type="text"
											placeholder="Your full name (e.g., John Smith)"
											value={emailName}
											onChange={(e) => setEmailName(e.target.value)}
											className="w-full"
										/>
										{hasName && emailDomain && hasCustomDomain && (
											<p className="text-xs text-emerald-600 flex items-center gap-1">
												<CheckCircle className="h-3 w-3" />
												Will send as: {emailName}@{emailDomain}
											</p>
										)}
									</div>

									{/* Email Signature */}
									<div className="space-y-2">
										<label className="text-sm font-medium text-gray-700">
											Email Signature
										</label>
										<Textarea
											value={emailSignature}
											onChange={(e) => setEmailSignature(e.target.value)}
											placeholder="Best regards,&#10;John Smith&#10;CEO at Company"
											rows={4}
											className="w-full"
										/>
										{hasSignature && (
											<p className="text-xs text-emerald-600 flex items-center gap-1">
												<CheckCircle className="h-3 w-3" />
												Professional signature configured
											</p>
										)}
									</div>
								</div>
							</div>

							{/* Warning/CTA Sections */}
							<div className="space-y-4">
								{/* Warning for oxide.email usage */}
								{onlyOxideEmail && !isSignedIn && (
									<div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
										<div className="text-center space-y-4">
											<div>
												<Gift className="h-10 w-10 mx-auto mb-3 text-emerald-600" />
												<h4 className="text-lg font-bold text-emerald-900 mb-2">
													Get Started Sending Free
												</h4>
											</div>

											{/* Benefits List */}
											<div className="bg-white/80 rounded-lg p-4 mb-4">
												<div className="grid grid-cols-1 gap-3 text-left">
													<div className="flex items-center gap-3">
														<div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
														<span className="text-sm font-medium text-gray-700">
															Free Sends on{' '}
															<a
																href="https://nos.email"
																target="_blank"
																rel="noopener noreferrer"
																className="text-emerald-600 hover:text-emerald-800 underline"
															>
																https://nos.email
															</a>{' '}
															cold infra
														</span>
													</div>
													<div className="flex items-center gap-3">
														<div className="w-2 h-2 bg-teal-500 rounded-full"></div>
														<span className="text-sm font-medium text-gray-700">
															Automated Leads Daily
														</span>
													</div>
													<div className="flex items-center gap-3">
														<div className="w-2 h-2 bg-teal-500 rounded-full"></div>
														<span className="text-sm font-medium text-gray-700">
															AI Remembers Your Preferences
														</span>
													</div>
													<div className="flex items-center gap-3">
														<div className="w-2 h-2 bg-orange-500 rounded-full"></div>
														<span className="text-sm font-medium text-gray-700">
															Custom Business Domain
														</span>
													</div>
													<div className="flex items-center gap-3">
														<div className="w-2 h-2 bg-pink-500 rounded-full"></div>
														<span className="text-sm font-medium text-gray-700">
															+ More
														</span>
													</div>
												</div>
											</div>

											<Button
												size="lg"
												className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 text-base"
												asChild
											>
												<Link href="/signup">
													<Gift className="h-5 w-5 mr-2" />
													Get Started Sending Free
												</Link>
											</Button>
											<p className="text-xs text-emerald-600 mt-2">
												⚠️ Trial emails may have limited deliverability
											</p>
										</div>
									</div>
								)}

								{/* No domain warning */}
								{!hasDomain && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-6">
										<div className="text-center space-y-4">
											<div>
												<ShoppingCart className="h-12 w-12 mx-auto mb-3 text-red-600" />
												<h3 className="text-xl font-bold mb-2 text-red-900">
													Custom Sending Domain Required
												</h3>
												<p className="text-sm leading-relaxed text-red-700">
													You need a custom domain to send professional
													emails. Purchase one to get started.
												</p>
											</div>
											<Button
												size="lg"
												className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3"
												asChild
											>
												<Link href={isSignedIn ? '/domains' : '/signup'}>
													<ShoppingCart className="h-5 w-5 mr-2" />
													{isSignedIn
														? 'Buy Professional Domain'
														: 'Sign Up & Buy Domain'}
													<ExternalLink className="h-4 w-4 ml-2" />
												</Link>
											</Button>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Fixed Footer with Send Button */}
						<div className="flex-shrink-0 flex items-center justify-between pt-4 border-t bg-white">
							<div className="text-sm text-gray-600">
								{isSelected
									? `${selectedCount} emails selected`
									: `${totalCount} emails ready to send`}
							</div>
							<div className="flex gap-2">
								<Button variant="outline" onClick={onClose}>
									Cancel
								</Button>
								<Button
									onClick={handleSend}
									disabled={!canSend || isEmailing}
									className="gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									<Send className="h-4 w-4" />
									{isSelected
										? `Send ${selectedCount} Selected`
										: `Send All ${totalCount}`}
								</Button>
							</div>
						</div>
					</>
				) : (
					// Sending Progress
					<div className="flex-1 flex flex-col justify-center space-y-6 py-8">
						<div className="text-center">
							<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
							<h3 className="text-lg font-semibold mb-2">Sending your emails...</h3>
							<p className="text-gray-600">
								Sending {isSelected ? selectedCount : totalCount} emails through
								your premium sending network
							</p>
						</div>

						<div className="space-y-2">
							<div className="flex justify-between text-sm">
								<span>Progress</span>
								<span>{Math.round(sendingProgress)}%</span>
							</div>
							<Progress value={sendingProgress} className="h-2" />
						</div>

						{sendingProgress === 100 && (
							<div className="text-center text-emerald-600">
								<CheckCircle className="h-8 w-8 mx-auto mb-2" />
								<p className="font-semibold">Emails sent successfully!</p>
							</div>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
